import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import { stepToEnglish } from "../stepEnglish";
import { useConfirm } from "./ConfirmModal";
import { XrayTestSteps } from "./TestCaseRow";
import ArtifactAiPanel, { useGenie } from "./ArtifactAiPanel";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Edit an Artifact's script.
 *
 * An Artifact is a list of steps (action, selector, value), not a source
 * file. The API shows them as Playwright JavaScript, one statement per step
 * under a "// Line N" comment per Cucumber line, and reads the text back into
 * steps (artifact_script_service): Validate (F6) asks it what each line is,
 * Save shows the changes first (Review your changes), then replaces the
 * steps in place.
 *
 * Laid out like the app's other dialogs (HelpDialog): a header with the
 * Artifact and where it sits, tabs, the code (Full script; or BDD-mapped: one block
 * per Cucumber line with Add step under each), the Inspector on the right for
 * the step under the cursor, the status and Save below.
 */

const PAD_Y = 10;
const lineHeightOf = (fontSize) => Math.round(fontSize * 1.6);

/* ------------------------------------------------------------------ */
/*  Highlighting                                                       */
/* ------------------------------------------------------------------ */
const TOKEN = /(\/\/.*$)|("(?:[^"\\]|\\.)*"?|'(?:[^'\\]|\\.)*'?|`(?:[^`\\]|\\.)*`?)|(\b(?:await|async|const|let|null|not|true|false)\b)|(\b(?:page|expect)\b)|([A-Za-z_$][\w$]*)(?=\s*\()|(-?\b\d+(?:\.\d+)?\b)/g;
const HEADER = /^\s*\/\/\s*(?:Line\s+\d+|Not tied to a scenario line)/i;

function StringBody({ text }) {
  /* {NAME} tokens stand out: they are filled in at Execute. */
  const parts = text.split(/(\{[A-Z0-9_]+\})/);
  return parts.map((p, i) =>
    /^\{[A-Z0-9_]+\}$/.test(p) ? (
      <span key={i} className="rounded-sm bg-amber-100 font-semibold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
        {p}
      </span>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    ),
  );
}

export function highlightLine(line) {
  if (HEADER.test(line)) return <span className="font-semibold text-brand-700">{line}</span>;
  const out = [];
  let last = 0;
  let m;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(line))) {
    if (m.index > last) out.push(<span key={last} className="text-slate-500">{line.slice(last, m.index)}</span>);
    const [text, comment, str, kw, obj, fn, num] = m;
    const key = m.index;
    if (comment) out.push(<span key={key} className="italic text-slate-400">{text}</span>);
    else if (str) out.push(<span key={key} className="text-emerald-700 dark:text-emerald-400"><StringBody text={text} /></span>);
    else if (kw) out.push(<span key={key} className="text-violet-700 dark:text-violet-400">{text}</span>);
    else if (obj) out.push(<span key={key} className="text-sky-700 dark:text-sky-400">{text}</span>);
    else if (fn) out.push(<span key={key} className="text-blue-700 dark:text-blue-400">{text}</span>);
    else if (num) out.push(<span key={key} className="text-orange-700 dark:text-orange-400">{text}</span>);
    else out.push(<React.Fragment key={key}>{text}</React.Fragment>);
    last = m.index + text.length;
    if (!text.length) TOKEN.lastIndex++;
  }
  if (last < line.length) out.push(<span key={last} className="text-slate-500">{line.slice(last)}</span>);
  return out;
}

/* The catalogue entry a line most likely is, from the names it uses (the
   Inspector's guess before Validate). */
export function guessAction(line, actions) {
  if (/^\s*\/\//.test(line)) return null;
  const names = new Set(line.match(/[A-Za-z_$][\w$]*/g) || []);
  const strings = line.match(/"[^"]*"|'[^']*'/g) || [];
  const common = new Set(["await", "page", "expect", "locator", "not"]);
  let best = null;
  for (const a of actions || []) {
    for (const t of [a.template, a.alt].filter(Boolean)) {
      const own = (t.replace(/\$[SV]/g, "").match(/[A-Za-z_][\w]*/g) || []).filter((n) => !common.has(n));
      const fixed = t.match(/"[^"]*"/g) || [];
      if (own.length && own.every((n) => names.has(n)) && fixed.every((f) => strings.some((s) => s.slice(1, -1) === f.slice(1, -1)))) {
        if (!best || own.length > best.n) best = { a, n: own.length };
      }
    }
  }
  return best?.a || null;
}

/* What Insert puts in: the template with empty quotes for its parts. */
export function snippetOf(template) {
  return template.replace(/\$S|\$V/g, '""');
}

/* The steps' serial numbers: every line that is code (not blank, not a //
   comment) is a step, numbered 1, 2, 3… in script order. {byLine: {scriptLine:
   n}, lines: [scriptLine…], total}. */
export function stepSerials(code) {
  const byLine = {};
  const lines = [];
  code.split("\n").forEach((l, i) => {
    if (l.trim() && !/^\s*\/\//.test(l)) {
      lines.push(i + 1);
      byLine[i + 1] = lines.length;
    }
  });
  return { byLine, lines, total: lines.length };
}

/* A line diff (longest common subsequence): [{type: "eq" | "add" | "del",
   a: old line no | null, b: new line no | null, text}]. Scripts are a few
   hundred lines, so the table stays small. */
export function diffLines(oldText, newText) {
  const a = oldText.split("\n");
  const b = newText.split("\n");
  const n = a.length;
  const m = b.length;
  const lcs = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--) lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
  const out = [];
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) {
      out.push({ type: "eq", a: i + 1, b: j + 1, text: a[i] });
      i++;
      j++;
    } else if (j < m && (i >= n || lcs[i][j + 1] >= lcs[i + 1][j])) {
      out.push({ type: "add", a: null, b: j + 1, text: b[j] });
      j++;
    } else {
      out.push({ type: "del", a: i + 1, b: null, text: a[i] });
      i++;
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  The code area: a textarea over its highlighted copy                */
/* ------------------------------------------------------------------ */
/* `block`: one block of the BDD-mapped view: as tall as its lines, its
   line numbers counted from `lineOffset` (its place in the whole script). */
function CodeArea({ value, onChange, readOnly, fontSize, problems, cursorLine, marks, flash, onCursor, textareaRef, scrollerRef, onKeyDown, block = false, lineOffset = 0, gutterDigits = 0, numbers = null, placeholder = "", label = "Artifact script" }) {
  const lineHeight = lineHeightOf(fontSize);
  const lines = useMemo(() => value.split("\n"), [value]);
  const byLine = useMemo(() => {
    const m = {};
    for (const p of problems || []) {
      if (!m[p.line] || p.severity === "error") m[p.line] = p.severity;
    }
    return m;
  }, [problems]);
  const marked = useMemo(() => new Set(marks || []), [marks]);
  const highlighted = useMemo(() => lines.map((l) => highlightLine(l)), [lines]);

  const report = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const before = ta.value.slice(0, ta.selectionStart);
    onCursor({ line: before.split("\n").length, col: ta.selectionStart - before.lastIndexOf("\n") });
  };
  const band = (n, cls, key) => (
    <div key={key} className={`pointer-events-none absolute inset-x-0 ${cls}`} style={{ top: PAD_Y + (n - 1) * lineHeight, height: lineHeight }} />
  );

  const font = { fontSize, lineHeight: `${lineHeight}px` };
  return (
    <div
      ref={scrollerRef}
      className={block ? "cq-scroll relative overflow-x-auto bg-card" : "cq-scroll relative min-h-0 flex-1 overflow-auto bg-card"}
      onClick={() => textareaRef.current?.focus()}
    >
      <div className={`relative flex w-max min-w-full ${block ? "" : "min-h-full"}`}>
        {/* Gutter: line numbers; a dot where Validate found a problem, a bar
            on the lines changed since the last save. */}
        <div
          className="sticky left-0 z-20 flex-none select-none border-r border-slate-200 bg-slate-50 pr-3 text-right font-mono text-slate-400"
          style={{ ...font, paddingTop: PAD_Y, paddingBottom: PAD_Y, width: `${Math.max(gutterDigits, String(lines.length + lineOffset).length) + 4}ch` }}
          aria-hidden="true"
        >
          {lines.map((_, i) => (
            <div key={i} className={`relative pl-5 ${i + 1 === cursorLine ? "font-semibold text-slate-800" : ""}`}>
              {marked.has(i + 1) && <span className="absolute inset-y-0 left-0 w-[3px] bg-brand-500" />}
              {byLine[i + 1] && (
                <span
                  className={`absolute left-2 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full ${byLine[i + 1] === "error" ? "bg-rose-500" : "bg-amber-400"}`}
                />
              )}
              {numbers ? numbers[i] : i + 1 + lineOffset}
            </div>
          ))}
        </div>
        <div className="relative min-w-0 flex-1">
          {cursorLine > 0 && band(cursorLine, "bg-slate-100/80", "cursor")}
          {[...marked].map((n) => band(n, "bg-brand-50 dark:bg-brand-500/10", `m${n}`))}
          {Object.entries(byLine).map(([n, sev]) =>
            band(Number(n), sev === "error" ? "bg-rose-50 dark:bg-rose-500/10" : "bg-amber-50 dark:bg-amber-500/10", `p${n}`),
          )}
          {flash?.line > 0 && band(flash.line, "bg-brand-200/80 dark:bg-brand-500/30", `flash${flash.at}`)}
          <pre className="relative m-0 whitespace-pre px-4 font-mono text-slate-800" style={{ ...font, paddingTop: PAD_Y, paddingBottom: PAD_Y }} aria-hidden="true">
            {highlighted.map((h, i) => (
              <div key={i} style={{ height: lineHeight }}>
                {!value && placeholder ? <span className="font-sans italic text-slate-400">{placeholder}</span> : h}
                {"​"}
              </div>
            ))}
          </pre>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              requestAnimationFrame(report);
            }}
            onKeyDown={(e) => {
              /* Tab indents (two spaces) in this area's own text. */
              if (e.key === "Tab" && !readOnly) {
                e.preventDefault();
                const ta = e.currentTarget;
                const s = ta.selectionStart;
                onChange(ta.value.slice(0, s) + "  " + ta.value.slice(ta.selectionEnd));
                requestAnimationFrame(() => {
                  ta.setSelectionRange(s + 2, s + 2);
                  report();
                });
                return;
              }
              onKeyDown?.(e);
            }}
            onKeyUp={report}
            onMouseUp={report}
            readOnly={readOnly}
            spellCheck={false}
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            wrap="off"
            aria-label={label}
            className="absolute inset-0 m-0 h-full w-full resize-none overflow-hidden whitespace-pre border-0 bg-transparent px-4 font-mono text-transparent caret-slate-900 outline-none selection:bg-brand-200/60 dark:caret-white"
            style={{ ...font, paddingTop: PAD_Y, paddingBottom: PAD_Y }}
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  BDD-mapped: the script as blocks, one per Cucumber line            */
/* ------------------------------------------------------------------ */
const HEADER_LINE = /^\s*\/\/\s*Line\s+(\d+)/i;
const UNTIED_LINE = /^\s*\/\/\s*Not tied to a scenario line/i;

/* The script cut at its "// Line N" (and "Not tied") comments, in script
   order: [{kind: "pre" | "line" | "untied", no, header, headerLine,
   bodyStart, body}]. joinBlocks puts them back exactly. A block shows its
   body without the one blank line that separates it from the next. */
export function splitBlocks(code) {
  const blocks = [];
  let cur = { kind: "pre", no: null, header: null, headerLine: null, bodyStart: 1, body: [] };
  code.split("\n").forEach((l, i) => {
    const m = HEADER_LINE.exec(l);
    if (m || UNTIED_LINE.test(l)) {
      blocks.push(cur);
      cur = { kind: m ? "line" : "untied", no: m ? Number(m[1]) : null, header: l, headerLine: i + 1, bodyStart: i + 2, body: [] };
    } else cur.body.push(l);
  });
  blocks.push(cur);
  return blocks;
}

export function joinBlocks(blocks) {
  return blocks.flatMap((b) => (b.header != null ? [b.header, ...b.body] : b.body)).join("\n");
}

export const blockText = (b) => (b.body.length && b.body[b.body.length - 1] === "" ? b.body.slice(0, -1) : b.body).join("\n");

/* The block's body after an edit of its shown text (its separator kept). */
export function withBlockText(b, text) {
  const sep = b.body.length && b.body[b.body.length - 1] === "" ? [""] : [];
  return { ...b, body: [...text.split("\n"), ...sep] };
}

const stepCountOf = (text) => text.split("\n").filter((l) => l.trim() && !/^\s*\/\//.test(l)).length;

function BlockView({ code, onChange, data, readOnly, fontSize, problems, changedLines, flash, cursorLine, onCursor, onKeyDown, focusRef, onAddBlock, onAddStep, serial }) {
  const blocks = useMemo(() => splitBlocks(code), [code]);
  const scenario = data.scenario || [];
  const lineOf = (no) => scenario.find((s) => Number(s.step_no) === Number(no));
  const refs = useRef({});
  const scrollerRef = useRef(null);
  const lineHeight = lineHeightOf(fontSize);
  const shown = blocks.map((b) => blockText(b));
  /* The script header is shown only when it holds more than comments. */
  const showPre = (b, i) => b.kind !== "pre" || stepCountOf(shown[i]) > 0;
  const missing = scenario.filter((s) => !blocks.some((b) => b.kind === "line" && b.no === Number(s.step_no)));

  const refOf = (i) => {
    refs.current[i] = refs.current[i] || React.createRef();
    return refs.current[i];
  };
  /* The block a script line is in, and its line there (a header goes to the block's first line). */
  const locate = (line) => {
    for (let i = blocks.length - 1; i >= 0; i--) {
      const b = blocks[i];
      if (b.headerLine != null && line === b.headerLine) return { i, local: 1 };
      if (line >= b.bodyStart) return { i, local: Math.min(line - b.bodyStart + 1, Math.max(1, shown[i].split("\n").length)) };
    }
    return { i: 0, local: 1 };
  };
  /* The dialog's caret moves (Insert, Ln, a problem, a change) land here. */
  focusRef.current = (line, col = 1, select = 0) => {
    const { i, local } = locate(line);
    const ta = refOf(i).current;
    if (!ta) return;
    const at = shown[i].split("\n").slice(0, local - 1).reduce((acc, l) => acc + l.length + 1, 0) + col - 1;
    ta.focus({ preventScroll: true });
    ta.setSelectionRange(at, at + select);
    const sc = scrollerRef.current;
    if (sc) {
      const y = ta.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop + PAD_Y + (local - 1) * lineHeight;
      if (y < sc.scrollTop + 40 || y > sc.scrollTop + sc.clientHeight - 60) sc.scrollTop = Math.max(0, y - sc.clientHeight / 3);
    }
  };

  const toLocal = (b, i, lines) => {
    const n = shown[i].split("\n").length;
    return (lines || []).map((l) => l - b.bodyStart + 1).filter((l) => l >= 1 && l <= n);
  };

  return (
    <div ref={scrollerRef} className="cq-scroll min-h-0 flex-1 overflow-y-auto bg-slate-50/60 p-4">
      {blocks.map((b, i) => {
        if (!showPre(b, i)) return null;
        const sc = b.kind === "line" ? lineOf(b.no) : null;
        const text = shown[i];
        const count = stepCountOf(text);
        const len = text.split("\n").length;
        const inBlock = cursorLine >= b.bodyStart && cursorLine < b.bodyStart + len;
        const mine = (problems || []).filter((p) => p.line >= b.bodyStart && p.line < b.bodyStart + len);
        const onHeader = (problems || []).filter((p) => p.line === b.headerLine);
        return (
          <section
            key={i}
            aria-label={b.kind === "line" ? `Scenario line ${b.no}` : b.kind === "untied" ? "Not tied to a scenario line" : "Script header"}
            className={`mb-3 rounded-xl border bg-card shadow-sm transition ${inBlock ? "border-brand-300 ring-1 ring-brand-300/60" : "border-slate-200"}`}
          >
            {/* The Cucumber line: what these steps automate. */}
            <header className={`flex items-start gap-3 rounded-t-xl border-b px-4 py-2.5 ${inBlock ? "border-brand-100 bg-brand-50/60" : "border-slate-100 bg-slate-50/60"}`}>
              <span className="mt-0.5 flex h-6 min-w-[1.5rem] flex-none items-center justify-center rounded-md bg-white px-1.5 text-[11px] font-bold text-slate-500 ring-1 ring-slate-200">
                {b.kind === "line" ? b.no : b.kind === "untied" ? "—" : "#"}
              </span>
              <div className="min-w-0 flex-1">
                {b.kind === "line" ? (
                  sc ? (
                    <p className="font-mono text-[13px] leading-snug">
                      <span className="font-semibold text-blue-700">{sc.keyword}</span> <span className="text-slate-800">{sc.action}</span>
                    </p>
                  ) : (
                    <p className="text-[13px] text-amber-700">Line {b.no} is not in the scenario.</p>
                  )
                ) : (
                  <p className="text-[13px] font-medium text-slate-600">{b.kind === "untied" ? "Not tied to a scenario line" : "Script header"}</p>
                )}
                {sc?.data && <StepData text={sc.data} />}
                {onHeader.map((p, k) => (
                  <p key={k} className={`mt-1 text-[12px] ${p.severity === "error" ? "text-rose-700" : "text-amber-700"}`}>
                    {p.message}
                  </p>
                ))}
              </div>
              <span className={`mt-0.5 flex-none rounded-full px-2 py-0.5 text-[11px] font-semibold ${count ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-700"}`}>
                {count ? `${count} step${count === 1 ? "" : "s"}` : "no steps"}
              </span>
            </header>
            <CodeArea
              block
              value={text}
              onChange={(t) => {
                const next = blocks.map((x, j) => (j === i ? withBlockText(x, t) : x));
                onChange(joinBlocks(next));
              }}
              readOnly={readOnly}
              fontSize={fontSize}
              lineOffset={b.bodyStart - 1}
              numbers={text.split("\n").map((_, k) => serial.byLine[b.bodyStart + k] || "")}
              gutterDigits={Math.max(2, String(serial.total).length)}
              problems={mine.map((p) => ({ ...p, line: p.line - b.bodyStart + 1 }))}
              cursorLine={inBlock ? cursorLine - b.bodyStart + 1 : 0}
              marks={toLocal(b, i, changedLines)}
              flash={flash && flash.line >= b.bodyStart && flash.line < b.bodyStart + len ? { ...flash, line: flash.line - b.bodyStart + 1 } : null}
              onCursor={(c) => onCursor({ line: b.bodyStart + c.line - 1, col: c.col })}
              textareaRef={refOf(i)}
              onKeyDown={onKeyDown}
              placeholder={readOnly ? "No steps." : "No steps for this line yet: type one, or Add a step below."}
              label={b.kind === "line" ? `Steps for scenario line ${b.no}` : "Steps"}
            />
            {!readOnly && (
              <div className="rounded-b-xl border-t border-slate-100 px-3 py-2">
                <AddStepMenu actions={data.actions} onAdd={(action) => onAddStep(action, b.bodyStart + len - 1)} label={b.kind === "line" ? `Add a step for line ${b.no}` : "Add a step"} />
              </div>
            )}
          </section>
        );
      })}
      {missing.length > 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 p-4">
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-slate-400">Scenario lines with no block</p>
          {missing.map((s) => (
            <div key={s.step_no} className="flex items-center gap-3 py-1">
              <span className="w-6 text-right font-mono text-[12px] text-slate-400">{s.step_no}</span>
              <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">
                <span className="font-semibold text-blue-700">{s.keyword}</span> <span className="text-slate-700">{s.action}</span>
              </span>
              {!readOnly && (
                <button type="button" onClick={() => onAddBlock(s)} className="rounded-lg border border-brand-200 px-2.5 py-1 text-[12px] font-semibold text-brand-700 hover:bg-brand-50">
                  Add steps
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Add step: a short menu of the runner's steps                       */
/* ------------------------------------------------------------------ */
const GROUP_ICON = { Navigate: "Link", Act: "Play", Dialogs: "ShieldCheck", Check: "Check", Values: "Variable" };
const iconOf = (entry) => Icons[GROUP_ICON[entry?.group]] || Icons.Script;

const MENU_W = 320; // w-80
const MENU_H = 360;

/* `toolbar`: styled like the toolbar's other buttons (a block's is dashed). */
function AddStepMenu({ actions, onAdd, label = "Add step", toolbar = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  /* Where the menu fits: under the button, or above it near the bottom;
     from its left edge, or its right edge near the dialog's right side.
     Opening past the dialog's edge scrolled the whole dialog sideways. */
  const [place, setPlace] = useState({ up: false, right: false });
  const ref = useRef(null);
  const inputRef = useRef(null);
  const toggle = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      const box = (el) => el?.getBoundingClientRect() || { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
      const across = box(ref.current.closest('[role="dialog"]'));
      const down = box(ref.current.closest(".cq-scroll") || ref.current.closest('[role="dialog"]'));
      const below = down.bottom - r.bottom;
      const above = r.top - down.top;
      setPlace({ right: r.left + MENU_W > across.right - 8, up: below < MENU_H && above > below });
    }
    setOpen((o) => !o);
    setQ("");
  };
  useEffect(() => {
    if (!open) return undefined;
    /* Focus the search box without scrolling anything to it. */
    inputRef.current?.focus({ preventScroll: true });
    const onDoc = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const needle = q.trim().toLowerCase();
  const groups = useMemo(() => {
    const g = new Map();
    for (const a of actions || []) {
      if (needle && !`${a.action} ${a.summary}`.toLowerCase().includes(needle)) continue;
      if (!g.has(a.group)) g.set(a.group, []);
      g.get(a.group).push(a);
    }
    return [...g.entries()];
  }, [actions, needle]);
  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        className={
          toolbar
            ? "inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-card px-2.5 text-[12.5px] font-semibold text-brand-700 shadow-sm transition hover:bg-slate-50"
            : "inline-flex items-center gap-1.5 rounded-lg border border-dashed border-brand-300 bg-card px-3 py-1.5 text-[12.5px] font-semibold text-brand-700 transition hover:border-brand-400 hover:bg-brand-50"
        }
      >
        <Icons.Plus className="h-4 w-4" /> {label}
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Choose a step"
          className={`absolute z-30 w-80 overflow-hidden rounded-xl border border-slate-200 bg-card shadow-xl ${place.right ? "right-0" : "left-0"} ${place.up ? "bottom-full mb-1" : "top-full mt-1"}`}
        >
          <label className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Icons.Search className="h-4 w-4 flex-none text-slate-400" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Find a step…"
              aria-label="Find a step"
              className="min-w-0 flex-1 bg-transparent text-[13px] text-slate-800 outline-none"
            />
          </label>
          <div className="cq-scroll max-h-72 overflow-y-auto p-1.5">
            {groups.length === 0 && <p className="px-2.5 py-2 text-[12.5px] text-slate-400">No step matches.</p>}
            {groups.map(([group, list]) => (
              <div key={group}>
                <p className="px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">{group}</p>
                {list.map((a) => {
                  const Icon = iconOf(a);
                  return (
                    <button
                      key={a.action}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setOpen(false);
                        onAdd(a.action);
                      }}
                      className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-1.5 text-left transition hover:bg-brand-50"
                    >
                      <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-md bg-brand-50 text-brand-700">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-mono text-[12.5px] font-semibold text-slate-800">{a.action}</span>
                        <span className="block text-[11.5px] leading-snug text-slate-500">{a.summary}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Right: the Inspector — the step under the cursor                   */
/* ------------------------------------------------------------------ */
function InspectorCard({ title, children, tone = "" }) {
  return (
    <section className={`rounded-xl border p-3 ${tone || "border-slate-200 bg-card"}`}>
      {title && <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">{title}</p>}
      {children}
    </section>
  );
}

function CodeValue({ children, masked }) {
  return (
    <div className="rounded-lg bg-slate-50 px-2.5 py-1.5 font-mono text-[12px] leading-relaxed text-slate-800 ring-1 ring-inset ring-slate-200 dark:bg-slate-800">
      {masked ? "••••••••" : <span className="break-all"><StringBody text={String(children)} /></span>}
    </div>
  );
}

function Inspector({ data, step, guess, stepNo, stepTotal, scenarioNo, problems, onPrev, onNext, onHide }) {
  const entry = step ? (data.actions || []).find((a) => a.action === step.action) : guess;
  const sc = (data.scenario || []).find((s) => Number(s.step_no) === Number(step ? step.source_step_no : scenarioNo));
  const Icon = iconOf(entry);
  const secretLiteral = step?.masked && step.value && !/\{[A-Z0-9_]+\}/.test(step.value);
  const navBtn =
    "inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-card text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 disabled:opacity-40";

  return (
    <aside aria-label="Inspector" className="flex h-full min-h-0 flex-col">
      <div className="flex h-12 flex-none items-center gap-2 border-b border-slate-200 px-4">
        <span className="flex-1 text-[13px] font-semibold text-slate-800">Inspector</span>
        {stepNo > 0 && <span className="text-[11.5px] text-slate-500">Step {stepNo} of {stepTotal}</span>}
        <button type="button" onClick={onPrev} disabled={!onPrev} className={navBtn} aria-label="Previous step" title="Previous step">
          <Icons.ChevronUp className="h-4 w-4" />
        </button>
        <button type="button" onClick={onNext} disabled={!onNext} className={navBtn} aria-label="Next step" title="Next step">
          <Icons.ChevronDown className="h-4 w-4" />
        </button>
        <button type="button" onClick={onHide} title="Hide the Inspector" aria-label="Hide the Inspector" className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">
          <Icons.ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="cq-scroll min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50/60 p-3">
        {entry ? (
          <>
            {/* What the step does. */}
            <section className="rounded-xl border border-brand-200 bg-card p-3 shadow-sm">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block font-mono text-[13.5px] font-semibold text-slate-900">{entry.action}</span>
                  <span className="block text-[11px] uppercase tracking-wider text-slate-400">{entry.group}</span>
                </span>
              </div>
              {step ? (
                <p className="mt-2.5 text-[14px] font-medium leading-snug text-slate-800">{stepToEnglish(step)}</p>
              ) : (
                <p className="mt-2.5 text-[12.5px] text-slate-500">Validate (F6) to read this line exactly.</p>
              )}
              <p className="mt-1.5 text-[12px] leading-snug text-slate-500">{entry.summary}</p>
            </section>

            {(problems || []).map((p, i) => (
              <div
                key={i}
                className={`flex gap-2 rounded-xl border p-2.5 text-[12.5px] ${p.severity === "error" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}
              >
                <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
                <span>{p.message}</span>
              </div>
            ))}

            {/* What it automates. */}
            <InspectorCard title="For scenario line">
              {sc ? (
                <>
                  <p className="flex gap-2 font-mono text-[12.5px] leading-snug">
                    <span className="flex-none text-slate-400">{sc.step_no}</span>
                    <span>
                      <span className="font-semibold text-blue-700">{sc.keyword}</span> <span className="text-slate-800">{sc.action}</span>
                    </span>
                  </p>
                  {sc.data && <StepData text={sc.data} />}
                </>
              ) : (
                <p className="text-[12.5px] text-slate-500">Not tied to a scenario line.</p>
              )}
            </InspectorCard>

            {step && (step.selector != null || step.value != null) && (
              <InspectorCard title="Details">
                <div className="space-y-2.5">
                  {step.selector != null && (
                    <div>
                      <p className="mb-1 text-[11.5px] font-medium text-slate-500">Selector</p>
                      <CodeValue>{step.selector || "—"}</CodeValue>
                    </div>
                  )}
                  {step.value != null && (
                    <div>
                      <p className="mb-1 text-[11.5px] font-medium text-slate-500">Value</p>
                      <CodeValue masked={secretLiteral}>{step.value === "" ? '""' : step.value}</CodeValue>
                    </div>
                  )}
                </div>
              </InspectorCard>
            )}

            {step && (step.masked || step.precondition_check) && (
              <div className="flex flex-wrap gap-1.5">
                {step.masked && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-medium text-slate-600" title="Shown and recorded with its name, never its value.">
                    <Icons.Lock className="h-3 w-3" /> Masked
                  </span>
                )}
                {step.precondition_check && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11.5px] font-medium text-violet-700" title="A failure here is Blocked: precondition not met.">
                    <Icons.JiraPrecondition className="h-3 w-3" /> Precondition check
                  </span>
                )}
              </div>
            )}

            <InspectorCard title="Written as">
              <pre className="whitespace-pre-wrap break-all font-mono text-[11.5px] text-slate-600">
                {entry.template}
                {entry.alt ? `\n${entry.alt}` : ""}
              </pre>
            </InspectorCard>
          </>
        ) : sc ? (
          <InspectorCard title={`Scenario line ${sc.step_no}`}>
            <p className="font-mono text-[12.5px] leading-snug">
              <span className="font-semibold text-blue-700">{sc.keyword}</span> <span className="text-slate-800">{sc.action}</span>
            </p>
            {sc.data && <StepData text={sc.data} />}
            <p className="mt-2 text-[12px] text-slate-500">The steps below this line's comment run for it.</p>
          </InspectorCard>
        ) : (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-200 bg-card px-4 py-8 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
              <Icons.Script className="h-5 w-5" />
            </span>
            <p className="text-[13px] font-medium text-slate-700">No step under the cursor</p>
            <p className="text-[12px] leading-snug text-slate-500">Click a step in the script to see what it does, the scenario line it is for, and its selector and value.</p>
          </div>
        )}
      </div>
    </aside>
  );
}

/* A step's data: a Gherkin table ("| a | b |" rows) as a table, else as written. */
export function StepData({ text }) {
  const rows = String(text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (!rows.length) return null;
  if (!rows.every((r) => r.startsWith("|") && r.endsWith("|"))) {
    return <span className="mt-0.5 block whitespace-pre-wrap text-[11.5px] text-slate-500">{text}</span>;
  }
  const cells = rows.map((r) => r.slice(1, -1).split("|").map((c) => c.trim()));
  return (
    <table className="mt-1 w-full border-collapse font-sans text-[11.5px]">
      <thead>
        <tr>
          {cells[0].map((c, i) => (
            <th key={i} className="border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-left font-semibold text-slate-600">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {cells.slice(1).map((row, r) => (
          <tr key={r}>
            {row.map((c, i) => (
              <td key={i} className="border border-slate-200 bg-card px-1.5 py-0.5 align-top text-slate-700">
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ------------------------------------------------------------------ */
/*  View changes: the saved script against the one being edited        */
/* ------------------------------------------------------------------ */
/* `onConfirm`: the review before Save — its own title, the errors that stop
   the save, and Save these changes. Without it, View changes. */
export function DiffDialog({ before, after, label, onClose, onGo, onConfirm, saving = false, errors = 0, warnings = 0, title = "", note = "" }) {
  const ops = useMemo(() => diffLines(before, after), [before, after]);
  const added = ops.filter((o) => o.type === "add").length;
  const removed = ops.filter((o) => o.type === "del").length;
  /* Unchanged runs longer than 6 lines fold to 3 lines of context each side. */
  const rows = [];
  for (let i = 0; i < ops.length; ) {
    if (ops[i].type !== "eq") {
      rows.push(ops[i++]);
      continue;
    }
    let j = i;
    while (j < ops.length && ops[j].type === "eq") j++;
    const run = ops.slice(i, j);
    const head = i === 0 ? 0 : 3;
    const tail = j === ops.length ? 0 : 3;
    if (run.length > head + tail + 1) {
      rows.push(...run.slice(0, head), { type: "fold", count: run.length - head - tail }, ...run.slice(run.length - tail));
    } else rows.push(...run);
    i = j;
  }
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);
  const review = Boolean(onConfirm);
  const heading = title || (review ? `Review your changes to ${label}` : `Changes to ${label}`);
  const tone = { add: "bg-emerald-50 text-emerald-900", del: "bg-rose-50 text-rose-900 line-through decoration-rose-300", eq: "text-slate-600" };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
      <button type="button" aria-label="Close the changes" onClick={onClose} className="absolute inset-0 cursor-default bg-slate-900/40" />
      <div role="dialog" aria-modal="true" aria-label={heading} className="relative z-10 flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-slate-200">
        <div className="flex flex-none items-center gap-3 border-b border-slate-200 px-6 py-4">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-700">
            <Icons.Layers className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold tracking-tight text-slate-900">{heading}</h3>
            <p className="text-[13px] text-slate-500">
              {note || (review ? "What Save will change in the saved script. " : "The saved script against yours. ")}
              <span className="font-semibold text-emerald-700">+{added}</span> <span className="font-semibold text-rose-700">−{removed}</span> lines
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close the changes" className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="cq-scroll min-h-0 flex-1 overflow-auto py-2 font-mono text-[12.5px]">
          {added + removed === 0 ? (
            <p className="px-6 py-4 font-sans text-[13px] text-slate-500">No changes: the script is as saved.</p>
          ) : (
            rows.map((r, k) =>
              r.type === "fold" ? (
                <div key={k} className="my-1 bg-slate-50 px-6 py-1 font-sans text-[11.5px] text-slate-400">
                  … {r.count} unchanged lines
                </div>
              ) : (
                <button
                  key={k}
                  type="button"
                  disabled={!r.b || !onGo}
                  onClick={() => onGo(r.b)}
                  title={r.b ? `Go to line ${r.b}` : "Removed"}
                  className={`grid w-full grid-cols-[3rem_3rem_1.25rem_1fr] text-left enabled:hover:brightness-95 ${tone[r.type]}`}
                >
                  <span className="select-none pr-2 text-right text-slate-400">{r.a || ""}</span>
                  <span className="select-none pr-2 text-right text-slate-400">{r.b || ""}</span>
                  <span className="select-none font-bold">{r.type === "add" ? "+" : r.type === "del" ? "−" : ""}</span>
                  <span className="whitespace-pre pr-4">{r.text || " "}</span>
                </button>
              ),
            )
          )}
        </div>
        <div className="flex flex-none items-center gap-3 border-t border-slate-200 bg-slate-50/60 px-6 py-3">
          {review &&
            (errors > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-rose-700">
                <Icons.Alert className="h-4 w-4" /> The script has {errors} error{errors === 1 ? "" : "s"}: fix {errors === 1 ? "it" : "them"} before saving.
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-600">
                <Icons.Check className="h-4 w-4 text-emerald-600" />
                Validated{warnings > 0 ? `, ${warnings} warning${warnings === 1 ? "" : "s"}` : ", no problems"}. The Artifact's current steps are kept as a previous version.
              </span>
            ))}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {review ? "Back to editing" : "Close"}
            </button>
            {review && (
              <button
                type="button"
                onClick={onConfirm}
                disabled={saving || errors > 0 || added + removed === 0}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50 disabled:shadow-none"
              >
                {saving ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.Check className="h-4 w-4" />}
                {saving ? "Saving…" : "Save these changes"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  The dialog                                                         */
/* ------------------------------------------------------------------ */
const TABS = [
  ["code", "Script", "Script"],
  ["scenario", "Scenario", "JiraXrayTest"],
];

function Crumb({ Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-slate-600">
      <Icon className="h-4 w-4" /> {text}
    </span>
  );
}

export default function ArtifactScriptEditor({ testcasekey, label, userkey, onClose, onSaved, initialLayout = "code" }) {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [code, setCode] = useState("");
  const [saved, setSaved] = useState("");
  const [check, setCheck] = useState(null); // {forCode, steps, problems, step_lines}
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pane, setPane] = useState("code");
  const fontSize = 14;
  const [inspector, setInspector] = useState(true);
  const [dock, setDock] = useState(false);
  const [cursor, setCursor] = useState({ line: 1, col: 1 });
  const [placed, setPlaced] = useState(false); // the user put the cursor somewhere
  const [flash, setFlash] = useState(null); // {line, at}: a line lit for a moment
  const textareaRef = useRef(null);
  const scrollerRef = useRef(null);
  const blockFocusRef = useRef(null); // set by the block view: put the caret at a script line
  const [ask, confirmDialog] = useConfirm();

  const readOnly = Boolean(data?.read_only);
  const dirty = code !== saved;
  const current = check && check.forCode === code ? check : null;
  const shown = current;
  const errors = (shown?.problems || []).filter((p) => p.severity === "error").length;
  const warnings = (shown?.problems || []).filter((p) => p.severity === "warning").length;
  /* The lines that differ from the saved script: marked in the gutter. */
  const changedLines = useMemo(
    () => (code === saved ? [] : diffLines(saved, code).filter((o) => o.type === "add").map((o) => o.b)),
    [code, saved],
  );
  /* Full script, or BDD-mapped: the script as blocks, one per Cucumber line,
     its steps under it numbered 1, 2, 3… across the blocks. */
  const [layout, setLayout] = useState(initialLayout);
  const [diffFor, setDiffFor] = useState(null); // {text, review, proposal}: View changes, the review before Save, or a proposal
  /* The AI assistant: shown or hidden (its chat is kept while the editor is
     open), and whether a proposal of it was applied since the last save. */
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMounted, setAiMounted] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  const aiWindow = useGenie(aiOpen);
  const [checking, setChecking] = useState(false); // Save is validating before the review

  const validate = useCallback(
    async (text) => {
      setValidating(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE_URL}testcases/${encodeURIComponent(testcasekey)}/script/validate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ script: text }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : `HTTP ${res.status}`);
        const out = { ...body.data, forCode: text };
        setCheck(out);
        if ((out.problems || []).length) setDock(true);
        return out;
      } catch (e) {
        setError(`Could not validate the script: ${e.message}`);
        return null;
      } finally {
        setValidating(false);
      }
    },
    [testcasekey],
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}testcases/${encodeURIComponent(testcasekey)}/script`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : `HTTP ${res.status}`);
        if (!alive) return;
        setData(body.data);
        setCode(body.data.script);
        setSaved(body.data.script);
        validate(body.data.script);
      } catch (e) {
        if (alive) setLoadErr(e.message || "Could not load the script.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [testcasekey, validate]);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 1200);
    return () => clearTimeout(t);
  }, [flash]);

  /* Save validates the script first: with errors, the problems open and the
     caret goes to the first; without, the review shows the changes, and Save
     these changes in it saves. */
  const review = async () => {
    if (readOnly || !dirty || saving || checking) return;
    setError("");
    setChecking(true);
    const text = code;
    const out = await validate(text);
    setChecking(false);
    if (!out) return;
    const errs = (out.problems || []).filter((p) => p.severity === "error");
    if (errs.length) {
      setDock(true);
      setError(`The script has ${errs.length} error${errs.length === 1 ? "" : "s"}: fix ${errs.length === 1 ? "it" : "them"}, then Save.`);
      goToLine(errs[0].line);
      return;
    }
    setDiffFor({ text, review: true });
  };
  const save = async () => {
    if (readOnly || !dirty || saving) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}testcases/${encodeURIComponent(testcasekey)}/script${userkey ? `?userkey=${encodeURIComponent(userkey)}` : ""}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ script: code, mode: aiApplied ? "ai" : "code" }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (res.status === 422 && body?.detail?.problems) {
        setCheck({ forCode: code, steps: [], step_lines: [], problems: body.detail.problems });
        setDiffFor(null);
        setDock(true);
        setError(body.detail.message);
        return;
      }
      if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : body?.detail?.message || `HTTP ${res.status}`);
      onSaved?.(body.data);
    } catch (e) {
      setDiffFor(null);
      setError(`Nothing was saved: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const close = async () => {
    if (dirty && !readOnly) {
      const ok = await ask({
        title: "Discard changes",
        message: `Discard your changes to the script of ${label}? The Artifact keeps the steps it has.`,
        confirmLabel: "Discard",
      });
      if (!ok) return;
    }
    onClose();
  };

  /* Keys: F6 validates, Ctrl+S opens the review before Save. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F6") {
        e.preventDefault();
        validate(code);
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (diffFor?.review) save();
        else review();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  /* ---- the editor's caret, scrolling and inserting ---- */
  const offsetOf = (text, line) => text.split("\n").slice(0, line - 1).reduce((acc, l) => acc + l.length + 1, 0);
  const scrollTo = (line) => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const top = PAD_Y + (line - 1) * lineHeightOf(fontSize);
    if (top < sc.scrollTop + 20 || top > sc.scrollTop + sc.clientHeight - 60) sc.scrollTop = Math.max(0, top - sc.clientHeight / 3);
  };
  /* Put the caret at (line, col) and bring it into view; `select` chars from there. */
  const placeCaret = (text, line, col = 1, select = 0) => {
    requestAnimationFrame(() => {
      if (layout === "split") {
        blockFocusRef.current?.(line, col, select);
        return;
      }
      const ta = textareaRef.current;
      if (!ta) return;
      const at = offsetOf(text, line) + col - 1;
      ta.focus({ preventScroll: true });
      ta.setSelectionRange(at, at + select);
      scrollTo(line);
    });
    setCursor({ line, col });
  };
  const flashLine = (line) => setFlash({ line, at: Date.now() });
  /* A scenario line with no block gets one, after the block of the line
     before it (else after the script header), with the caret in it. */
  const addBlock = (s) => {
    const no = Number(s.step_no);
    const blocks = splitBlocks(code);
    let at = 0;
    blocks.forEach((b, i) => {
      if (b.kind === "line" && b.no < no) at = i;
    });
    const prev = blocks[at];
    if (!prev.body.length || prev.body[prev.body.length - 1] !== "") blocks[at] = { ...prev, body: [...prev.body, ""] };
    const header = `// Line ${no} · ${[s.keyword, s.action].filter(Boolean).join(" ")}`;
    const text = joinBlocks([...blocks.slice(0, at + 1), { kind: "line", no, header, body: ["", ""] }, ...blocks.slice(at + 1)]);
    setCode(text);
    const line = splitBlocks(text).find((b) => b.kind === "line" && b.no === no).bodyStart;
    setPlaced(true);
    placeCaret(text, line, 1);
    flashLine(line);
  };
  /* View changes: the saved script against this one. */
  const viewChanges = () => setDiffFor({ text: code, review: false });
  /* The assistant's proposal: shown against the editor's script, or put in
     the editor (asking first when that drops edits not saved yet). */
  const viewProposal = (text) => setDiffFor({ text, review: false, proposal: true });
  const applyProposal = async (text) => {
    if (readOnly) return false;
    if (dirty) {
      const ok = await ask({
        title: "Apply the proposal",
        message: "Apply the assistant's script? It replaces the whole script in the editor, your changes not saved yet included. Save still shows the changes first.",
        confirmLabel: "Apply",
      });
      if (!ok) return false;
    }
    const first = diffLines(code, text).find((o) => o.type === "add");
    setCode(text);
    setAiApplied(true);
    if (first) goToLine(first.b);
    validate(text);
    return true;
  };
  const toggleAi = () => {
    setAiMounted(true);
    setAiOpen((o) => !o);
  };
  /* Go to a line (an Ln chip, a problem, a scenario line, a change): the
     caret moves there and the line is lit for a moment. */
  const goToLine = (n) => {
    setPlaced(true);
    placeCaret(code, n, 1);
    flashLine(n);
  };

  const onUserCursor = (c) => {
    setCursor(c);
    setPlaced(true);
  };

  /* Add a step: its template on a new line, the caret in its first "".
     `afterLine` is a block's last line (an empty block's line is filled);
     without it, below the cursor (at the end when it was never placed). */
  const addStep = (actionId, afterLine) => {
    if (readOnly) return;
    const entry = (data.actions || []).find((a) => a.action === actionId);
    if (!entry) return;
    const snippet = snippetOf(entry.template);
    const lines = code.split("\n");
    let newLine;
    if (afterLine != null && !(lines[afterLine - 1] || "").trim()) {
      lines[afterLine - 1] = snippet;
      newLine = afterLine;
    } else {
      let after = afterLine ?? (placed ? cursor.line : lines.length);
      while (after > 0 && after === lines.length && !lines[after - 1].trim()) after -= 1; // not below trailing blank lines
      lines.splice(after, 0, snippet);
      newLine = after + 1;
    }
    const next = lines.join("\n");
    const q = snippet.indexOf('""');
    setCode(next);
    flashLine(newLine);
    setPlaced(true);
    placeCaret(next, newLine, q === -1 ? snippet.length + 1 : q + 2);
  };

  /* The Inspector's step: the validated one on the cursor's line (else the
     step the line looks like), its place among the steps, the scenario line
     its block is for, and the problems on its line. */
  const serial = useMemo(() => stepSerials(code), [code]);
  const prevStepLine = [...serial.lines].reverse().find((n) => n < cursor.line);
  const nextStepLine = serial.lines.find((n) => n > cursor.line);
  const blockNo = useMemo(() => {
    const b = [...splitBlocks(code)].reverse().find((x) => (x.headerLine ?? 0) <= cursor.line);
    return b?.kind === "line" ? b.no : null;
  }, [code, cursor.line]);

  const lineText = code.split("\n")[cursor.line - 1] || "";
  const stepHere = current ? current.steps?.[(current.step_lines || []).indexOf(cursor.line)] : null;
  const guessHere = !stepHere && placed ? guessAction(lineText, data?.actions) : null;
  const stepCount = current ? current.steps.length : (data?.steps || []).length;
  const title = data ? data.name || data.xray_test_key : label;

  const statusText = validating
    ? "Validating…"
    : !shown
      ? "Not validated — F6"
      : errors
        ? `${errors} error${errors === 1 ? "" : "s"}${warnings ? `, ${warnings} warning${warnings === 1 ? "" : "s"}` : ""}`
        : `Valid · ${stepCount} step${stepCount === 1 ? "" : "s"}${warnings ? ` · ${warnings} warning${warnings === 1 ? "" : "s"}` : ""}`;
  const statusTone = !shown || validating ? "bg-slate-100 text-slate-600" : errors ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";

  const toolBtn = "inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-card px-2.5 text-[12.5px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Edit the script of ${label}`}
        className="relative z-10 flex h-[94vh] w-[96vw] min-w-[960px] flex-col overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-slate-200"
      >
        {/* Header: what is edited and where it sits, as the app's dialogs do. */}
        <div className="flex flex-none items-start gap-3.5 border-b border-slate-200 px-6 py-4">
          <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-700">
            <Icons.Script className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="min-w-0 truncate text-lg font-semibold tracking-tight text-slate-900" title={title}>
                Edit script · {title}
              </h2>
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11.5px] font-semibold text-brand-700 ring-1 ring-brand-200">
                {stepCount} step{stepCount === 1 ? "" : "s"}
              </span>
              {data?.hand_edited && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-medium text-slate-600"
                  title={`Edited by hand ${String(data.hand_edited.at || "").slice(0, 16).replace("T", " ")}`}
                >
                  <Icons.Pencil className="h-3 w-3" /> Edited by hand
                </span>
              )}
              {readOnly && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11.5px] font-medium text-amber-700 ring-1 ring-amber-200" title={data.read_only}>
                  <Icons.Lock className="h-3 w-3" /> Read-only
                </span>
              )}
            </div>
            {data && (
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1" aria-label="Where it sits">
                {data.epic_key && <Crumb Icon={Icons.JiraEpic} text={data.epic_key} />}
                {data.epic_key && data.story_key && <Icons.ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
                {data.story_key && <Crumb Icon={Icons.JiraStory} text={data.story_key} />}
                {(data.epic_key || data.story_key) && data.xray_test_key && <Icons.ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
                {data.xray_test_key && <Crumb Icon={Icons.JiraXrayTest} text={data.xray_test_key} />}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close the editor"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        {readOnly && <p className="flex-none border-b border-amber-200 bg-amber-50 px-6 py-2 text-[12.5px] text-amber-800">{data.read_only}</p>}

        {/* Tabs (as on Settings), with the tools of the open tab on the right. */}
        <div className="flex flex-none items-center gap-3 border-b border-slate-200 px-4">
          <div role="tablist" aria-label="Artifact editor" className="flex gap-1">
            {TABS.map(([id, text, icon]) => {
              const Icon = Icons[icon];
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={pane === id}
                  disabled={!data || validating}
                  onClick={() => setPane(id)}
                  className={[
                    "-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition",
                    pane === id ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-800",
                  ].join(" ")}
                >
                  <Icon className="h-4 w-4" /> {text}
                </button>
              );
            })}
          </div>
          {data && pane === "code" && (
            <div className="ml-auto flex items-center gap-2 py-1.5">
              <div role="radiogroup" aria-label="Layout" className="inline-flex rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800">
                {[
                  ["code", "Full script", "The whole script as one file, each BDD step as a // Line N comment"],
                  ["split", "BDD-mapped", "Each BDD (Cucumber) step with its Playwright steps under it, numbered 1, 2, 3…"],
                ].map(([id, text, hint]) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={layout === id}
                    title={hint}
                    onClick={() => setLayout(id)}
                    className={`rounded-md px-2.5 py-1 text-[12.5px] font-semibold transition ${layout === id ? "bg-white text-brand-700 shadow-sm dark:bg-slate-700" : "text-slate-500 hover:text-slate-800"}`}
                  >
                    {text}
                  </button>
                ))}
              </div>
              {layout === "code" && !readOnly && <AddStepMenu toolbar actions={data.actions} onAdd={(action) => addStep(action)} label="Add step" />}
              <button type="button" onClick={() => validate(code)} disabled={validating} className={`${toolBtn} gap-1.5`}>
                <Icons.Check className="h-4 w-4 text-brand-600" /> {validating ? "Validating…" : "Validate (F6)"}
              </button>
              {/* The AI agent's own button: a gradient pill with a light sweep on hover. */}
              <button
                type="button"
                onClick={toggleAi}
                aria-pressed={aiOpen}
                title="Ask the AI assistant about this script"
                className={[
                  "group relative inline-flex h-8 items-center gap-1.5 overflow-hidden rounded-full bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500 pl-2 pr-3.5 text-[12.5px] font-semibold text-white shadow-md shadow-brand-500/30 transition hover:shadow-lg hover:shadow-brand-500/40",
                  aiOpen ? "ring-2 ring-brand-300 ring-offset-1 ring-offset-card" : "",
                ].join(" ")}
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                />
                <span aria-hidden="true" className="relative flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                  <Icons.Spark className="h-3.5 w-3.5" />
                </span>
                <span className="relative">AI assistant</span>
              </button>
            </div>
          )}
        </div>

        {loadErr ? (
          <p className="m-6 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{loadErr}</p>
        ) : !data ? (
          <p className="m-6 flex items-center gap-2 text-sm text-slate-500">
            <Icons.Spinner className="h-4 w-4 animate-spin" /> Loading the script…
          </p>
        ) : pane === "scenario" ? (
          <div className="cq-scroll min-h-0 flex-1 overflow-y-auto bg-slate-50/60 p-6">
            <div className="mx-auto max-w-4xl space-y-4">
              {(data.preconditions || []).length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-card p-4">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-800">
                    <Icons.JiraPrecondition className="h-4 w-4" /> Preconditions
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-[13px] text-slate-700">
                    {data.preconditions.map((p, i) => (
                      <li key={i}>{typeof p === "string" ? p : p.text || JSON.stringify(p)}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="rounded-xl border border-slate-200 bg-card">
                <XrayTestSteps steps={data.scenario} />
              </div>
              <p className="text-center text-[12px] text-slate-400">The scenario comes from Jira and is read only here. Each step of the script is tied to one of its lines.</p>
            </div>
          </div>
        ) : (
          <div className="relative flex min-h-0 flex-1">
            <div className="flex min-w-0 flex-1 flex-col">
              {layout === "split" ? (
                <BlockView
                  code={code}
                  onChange={setCode}
                  data={data}
                  readOnly={readOnly}
                  fontSize={fontSize}
                  problems={current?.problems}
                  changedLines={changedLines}
                  flash={flash}
                  cursorLine={placed ? cursor.line : 0}
                  onCursor={onUserCursor}
                  focusRef={blockFocusRef}
                  onAddBlock={addBlock}
                  onAddStep={addStep}
                  serial={serial}
                />
              ) : (
                <CodeArea
                  value={code}
                  onChange={setCode}
                  readOnly={readOnly}
                  fontSize={fontSize}
                  problems={current?.problems}
                  cursorLine={cursor.line}
                  marks={changedLines}
                  flash={flash}
                  onCursor={onUserCursor}
                  textareaRef={textareaRef}
                  scrollerRef={scrollerRef}
                />
              )}
              {/* Problems */}
              <div className="flex-none border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setDock((d) => !d)}
                  aria-expanded={dock}
                  className="flex w-full items-center gap-2 bg-slate-50/60 px-3 py-2 text-left text-[11.5px] font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-100"
                >
                  <Icons.Alert className="h-4 w-4 text-slate-400" />
                  Errors &amp; warnings
                  {current && (errors > 0 || warnings > 0) && (
                    <span className="normal-case tracking-normal">
                      {errors > 0 && <span className="ml-1 rounded-full bg-rose-100 px-1.5 text-rose-700">{errors}</span>}
                      {warnings > 0 && <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-amber-700">{warnings}</span>}
                    </span>
                  )}
                  <Icons.ChevronUp className={`ml-auto h-4 w-4 transition ${dock ? "rotate-180" : ""}`} />
                </button>
                {dock && (
                  <ul className="cq-scroll max-h-40 overflow-y-auto py-1 text-[12.5px]">
                    {!current ? (
                      <li className="px-4 py-1 text-slate-400">Not validated: press F6.</li>
                    ) : current.problems.length === 0 ? (
                      <li className="px-4 py-1 text-slate-500">No problems: every line is a step the runner knows.</li>
                    ) : (
                      current.problems.map((p, i) => (
                        <li key={i}>
                          <button type="button" onClick={() => goToLine(p.line)} className="flex w-full items-start gap-2 px-4 py-1 text-left hover:bg-slate-100">
                            <span className={`mt-1.5 h-2 w-2 flex-none rounded-full ${p.severity === "error" ? "bg-rose-500" : "bg-amber-400"}`} />
                            <span className="flex-none font-mono text-slate-400">
                              {layout === "split" && serial.byLine[p.line] ? `Step ${serial.byLine[p.line]}` : `Ln ${p.line}`}
                            </span>
                            <span className="min-w-0 text-slate-700">{p.message}</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            </div>

            {inspector ? (
              <div className="w-[21rem] flex-none border-l border-slate-200 bg-card">
                <Inspector
                  data={data}
                  step={stepHere}
                  guess={guessHere}
                  stepNo={serial.byLine[cursor.line] || 0}
                  stepTotal={serial.total}
                  scenarioNo={blockNo}
                  problems={(current?.problems || []).filter((p) => p.line === cursor.line)}
                  onPrev={prevStepLine ? () => goToLine(prevStepLine) : null}
                  onNext={nextStepLine ? () => goToLine(nextStepLine) : null}
                  onHide={() => setInspector(false)}
                />
              </div>
            ) : (
              <div className="flex w-11 flex-none flex-col items-center gap-3 border-l border-slate-200 bg-slate-50/60 py-3">
                <button
                  type="button"
                  onClick={() => setInspector(true)}
                  aria-label="Show the Inspector"
                  title="Show the Inspector"
                  className="rounded-md p-1.5 text-slate-500 transition hover:bg-white hover:text-slate-800"
                >
                  <Icons.ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 [writing-mode:vertical-rl]">Inspector</span>
              </div>
            )}
            {aiMounted && (
              <div
                hidden={!aiWindow.shown}
                className={`absolute bottom-4 right-4 z-30 h-[min(620px,calc(100%-2rem))] w-[24rem] max-w-[calc(100%-2rem)] overflow-hidden rounded-2xl border border-slate-300 bg-card shadow-2xl shadow-slate-900/30 dark:border-slate-600 ${aiWindow.closing ? "cq-genie-out-tr" : "cq-genie-in-tr"}`}
              >
                <ArtifactAiPanel
                  testcasekey={testcasekey}
                  label={data.xray_test_key || label}
                  code={code}
                  readOnly={readOnly}
                  onApply={applyProposal}
                  onViewProposal={viewProposal}
                  onHide={() => setAiOpen(false)}
                />
              </div>
            )}
          </div>
        )}

        {/* Footer: the check, the keys, Cancel and Save (as HelpDialog's footer). */}
        <div className="flex flex-none items-center gap-3 border-t border-slate-200 bg-slate-50/60 px-6 py-3">
          {data && pane !== "scenario" && (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ${statusTone}`}>
              {validating ? <Icons.Spinner className="h-3.5 w-3.5 animate-spin" /> : errors ? <Icons.Alert className="h-3.5 w-3.5" /> : shown ? <Icons.Check className="h-3.5 w-3.5" /> : null}
              {statusText}
            </span>
          )}
          {data && pane !== "scenario" && (
            <span className="font-mono text-[12px] text-slate-500" title="Where the caret is">
              {layout === "split"
                ? serial.byLine[cursor.line] && placed
                  ? `Step ${serial.byLine[cursor.line]} of ${serial.total}`
                  : `${serial.total} steps`
                : `Ln ${cursor.line}, Col ${cursor.col}`}
            </span>
          )}
          {dirty && !readOnly && (
            <>
              <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-amber-700">
                <span className="h-3 w-[3px] rounded-full bg-brand-500" />
                {changedLines.length > 0 ? `${changedLines.length} changed line${changedLines.length === 1 ? "" : "s"}` : "Unsaved changes"}
              </span>
              <button
                type="button"
                onClick={viewChanges}
                disabled={validating}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                <Icons.Layers className="h-4 w-4 text-brand-600" /> View changes
              </button>
            </>
          )}
          {error ? (
            <span className="min-w-0 truncate text-[12.5px] text-rose-700" title={error}>
              {error}
            </span>
          ) : (
            data &&
            !readOnly && <span className="hidden min-w-0 truncate text-[12px] text-slate-400 lg:inline">F6 validates · Ctrl+S saves · Save validates and shows your changes first.</span>
          )}
          <div className="ml-auto flex flex-none items-center gap-2">
            <button
              type="button"
              onClick={close}
              className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && data && (
              <button
                type="button"
                onClick={review}
                disabled={!dirty || saving || validating || checking}
                title="Validate, see your changes, then save them"
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50 disabled:shadow-none"
              >
                {saving || checking ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.Check className="h-4 w-4" />}
                {saving ? "Saving…" : checking ? "Validating…" : "Save"}
              </button>
            )}
          </div>
        </div>
      </div>
      {diffFor && (
        <DiffDialog
          before={diffFor.proposal ? code : saved}
          title={diffFor.proposal ? "Changes the assistant proposes" : ""}
          note={diffFor.proposal ? "The editor's script against the proposal. Nothing changes until you apply it. " : ""}
          warnings={diffFor.review ? warnings : 0}
          after={diffFor.text}
          label={data?.xray_test_key || label}
          onClose={() => setDiffFor(null)}
          onGo={
            pane === "code" && !saving && !diffFor.proposal
              ? (n) => {
                  setDiffFor(null);
                  goToLine(n);
                }
              : null
          }
          onConfirm={diffFor.review ? save : null}
          saving={saving}
          errors={diffFor.review ? errors : 0}
        />
      )}
      {confirmDialog}
    </div>
  );
}
